import type { JSX } from 'react';
import { IconPlayerPause, IconPlayerPlay, IconX } from '@tabler/icons-react';
import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ActionIcon, Box, Group, Progress, Text } from '@mantine/core';
import { useDeviceType } from '../../../hooks';
import { useAudioPlayer } from '../../../hooks/useAudioPlayer';
import { miniPlayerDesktopVariants, miniPlayerMobileVariants } from '../../../motion';
import classes from './MiniPlayer.module.css';

function MiniPlayer(): JSX.Element {
  const { state, togglePlay, closePlayer, seek } = useAudioPlayer();
  const { isMobile, isTablet } = useDeviceType();
  const isMobileView = isMobile || isTablet;
  const prefersReducedMotion = useReducedMotion();

  const isVisible = state.isMiniPlayerVisible && !!state.track;

  const progress = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newProgress = clickX / rect.width;
    const newTime = newProgress * state.duration;
    seek(newTime);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const variants = isMobileView ? miniPlayerMobileVariants : miniPlayerDesktopVariants;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="mini-player"
          className={classNames(classes.miniPlayer, {
            [classes.mobile]: isMobileView,
            [classes.desktop]: !isMobileView,
          })}
          variants={prefersReducedMotion ? undefined : variants}
          initial={prefersReducedMotion ? undefined : 'hidden'}
          animate={prefersReducedMotion ? undefined : 'visible'}
          exit={prefersReducedMotion ? undefined : 'exit'}
        >
          <Group className={classes.content} gap="sm" wrap="nowrap">
            <Box className={classes.trackInfo}>
              <Text size="sm" fw={500} lineClamp={1} className={classes.title}>
                {state.track!.title}
              </Text>
              {!isMobileView && (
                <Text size="xs" c="dimmed">
                  {state.track!.date}
                </Text>
              )}
            </Box>

            {isMobileView && (
              <Text size="xs" c="dimmed" className={classes.time}>
                {formatTime(state.currentTime)}
              </Text>
            )}

            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                color="accent"
                variant="filled"
                onClick={togglePlay}
                size="lg"
                loading={state.isLoading}
              >
                {state.isPlaying ? <IconPlayerPause size={20} /> : <IconPlayerPlay size={20} />}
              </ActionIcon>

              <ActionIcon color="primary" variant="subtle" onClick={closePlayer} size="md">
                <IconX size={18} />
              </ActionIcon>
            </Group>
          </Group>

          <Box className={classes.progressContainer} onClick={handleProgressClick}>
            <Progress value={progress} size="xs" color="accent" className={classes.progress} />
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default MiniPlayer;
